import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { exportMoListToExcel } from '../../utils/moExcel';
import {
  Plus, Eye, Edit, Trash2, FileSpreadsheet, Send, FolderOpen, RefreshCw, X
} from 'lucide-react';

const STATUS_BADGES = {
  draft:     'bg-gray-100 text-gray-700 border-gray-300',
  submitted: 'bg-amber-100 text-amber-700 border-amber-300',
  approved:  'bg-green-100 text-green-700 border-green-300',
  rejected:  'bg-red-100 text-red-700 border-red-300',
};
const STATUS_LABELS = { draft: 'Draft', submitted: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' };
const TABS = ['all', 'draft', 'submitted', 'approved', 'rejected'];

export default function MainPOListPage() {
  const { canWrite, canEdit, canDelete } = usePermission('main_po');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/worker') ? '/worker/main-po' : '/admin/main-po';

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Orders in the current tab + all their item rows
  const exportExcel = async () => {
    if (!rows.length) return toast.error('No Manufacturing Orders to export');
    setExporting(true);
    try {
      const res = await api.get('/main-pos/export-items', { params: { ids: rows.map(r => r.id).join(',') } });
      await exportMoListToExcel(rows, res.data || [], status === 'all' ? 'All' : STATUS_LABELS[status] || status);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/main-pos', { params: { status } });
      setRows(res.data.rows || []);
      const map = {};
      (res.data.counts || []).forEach(c => { map[c.status] = c.c; });
      map.all = Object.values(map).reduce((a, b) => a + b, 0);
      setCounts(map);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Manufacturing Orders');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/main-pos/${deleteTarget.id}`);
      toast.success('Manufacturing Order deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-blue-600" /> Manufacturing Order
          </h1>
          <p className="text-sm text-gray-500">Production orders — approved Manufacturing Orders automatically create Projects</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title="Refresh">
            <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={exportExcel} disabled={exporting || loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-sm font-semibold text-emerald-700 disabled:opacity-50">
            <FileSpreadsheet className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
          {canWrite && (
            <button onClick={() => navigate(`${basePath}/new`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">
              <Plus className="w-4 h-4" /> New Manufacturing Order
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map(t => (
          <button key={t} onClick={() => setStatus(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              status === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}>
            {t === 'all' ? 'All' : STATUS_LABELS[t]} {counts[t] !== undefined ? `(${counts[t]})` : ''}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-400 text-sm">No Manufacturing Orders found</div>
        ) : (
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-3">PO No.</th>
                <th className="px-4 py-3">Project Name</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Order Date</th>
                <th className="px-4 py-3 text-center">Items</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Linked Project</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(po => {
                const editable = ['draft', 'rejected'].includes(po.status) &&
                  (canEdit || (canWrite && po.created_by === user?.id));
                return (
                  <tr key={po.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-blue-700">
                      {po.po_no}
                      {po.proto_mode && <div className={`mt-0.5 text-[10px] font-semibold ${po.proto_mode === 'auto' ? 'text-purple-600' : 'text-slate-500'}`}>Proto: {po.proto_mode === 'auto' ? 'Automatic' : 'Manual'}</div>}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">{po.project_name}</td>
                    <td className="px-4 py-3 text-gray-600">{po.client_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{po.order_date ? new Date(po.order_date).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{po.item_count}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_BADGES[po.status]}`}>
                        {STATUS_LABELS[po.status]}
                      </span>
                      {po.status === 'rejected' && po.rejected_reason && (
                        <p className="text-xs text-red-500 mt-1 max-w-[180px] truncate" title={po.rejected_reason}>
                          {po.rejected_reason}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {po.linked_project_code ? (
                        <span className="inline-flex items-center gap-1 text-green-700 font-semibold text-xs">
                          <FolderOpen className="w-3.5 h-3.5" /> {po.linked_project_code}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => navigate(`${basePath}/${po.id}`)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" title="View">
                          <Eye className="w-4 h-4" />
                        </button>
                        {editable && (
                          <button onClick={() => navigate(`${basePath}/${po.id}?edit=1`)}
                            className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600" title="Edit">
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete && po.status === 'draft' && (
                          <button onClick={() => setDeleteTarget(po)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-800">Delete Manufacturing Order?</h3>
              <button onClick={() => setDeleteTarget(null)}><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-600">
              Delete draft <strong>{deleteTarget.po_no}</strong> ({deleteTarget.project_name})? All item rows and images will be removed. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm rounded-lg border border-gray-200">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
